import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "./ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Bell, Search, User } from "lucide-react";
import SonomaLogo from "../assets/sonoma-rounded.png";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { DarkModeToggle } from "@/components/theme/DarkModeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  signOut,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  GoogleAuthProvider,
  getIdTokenResult,
} from "firebase/auth";
import { useDispatch, useSelector } from "react-redux";
import { auth, db, functions } from "@/firebase/config";
import { setUser } from "@/State/usersSlice";
import { doc, deleteDoc } from "firebase/firestore";
import { useToast } from "@/components/ui/use-toast";
import { httpsCallable } from "firebase/functions";

interface HeaderProps {
  className?: string;
}

  const Header = ({ className }: HeaderProps) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [isSwitchingView, setIsSwitchingView] = useState(false);
  const [showSwitchModal, setShowSwitchModal] = useState(false);

  // Get current user's schoolID from Redux
  const currentUser = useSelector((state: any) => state.users?.currentUser);
  const schoolID = currentUser?.selectedAffiliation?.schoolID;

  const handleSwitchToStaffView = async () => {
    if (!schoolID || isSwitchingView) return;
    setIsSwitchingView(true);
    try {
      const switchAdminViewFn = httpsCallable(functions, 'switchAdminView');
      await switchAdminViewFn({ targetView: 'school_staff', schoolID });
      if (auth.currentUser) {
        await getIdTokenResult(auth.currentUser, true);
      }
      localStorage.setItem('portal', 'school_staff');
      window.location.href = '/faculty-dashboard';
    } catch (error: any) {
      console.error('Switch view error:', error);
      toast({
        title: "Switch Failed",
        description: error.message || "Could not switch view.",
        variant: "destructive",
      });
      setIsSwitchingView(false);
      setShowSwitchModal(false);
    }
  };

  // Search State
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [suggestion, setSuggestion] = useState<{ route: string; label: string; reason: string } | null>(null);

  const clearAllStorage = async () => {
    localStorage.clear();
    sessionStorage.clear();
    if (indexedDB?.databases) {
      const dbs = await indexedDB.databases();
      dbs.forEach((db) => {
        if (db.name) indexedDB.deleteDatabase(db.name);
      });
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      keys.forEach((key) => caches.delete(key));
    }
  };

  const handleLogout = async () => {
    setShowLogoutDialog(true);
  };

  const confirmLogout = async () => {
    const user = auth.currentUser;
    if (!user) {
      setShowLogoutDialog(false);
      return;
    }
    try {
      setIsLoggingOut(true);
      localStorage.setItem("loggingOut", "true");
      await clearAllStorage();
      await signOut(auth);
      dispatch(setUser(null));
      toast({
        title: "Logged Out",
        description: "You have been successfully logged out.",
        duration: 3000,
      });
      window.location.assign("/admin-login");
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        title: "Logout Failed",
        description: "An error occurred during logout. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsLoggingOut(false);
      localStorage.removeItem("loggingOut");
      setShowLogoutDialog(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!confirm("Are you sure you want to permanently delete your account? This action cannot be undone.")) return;

    const user = auth.currentUser;
    if (!user) return;

    try {
      setIsDeletingAccount(true);
      localStorage.setItem("loggingOut", "true");

      // Reauthenticate based on provider
      const providerId = user.providerData[0]?.providerId;
      if (providerId === "google.com") {
        const provider = new GoogleAuthProvider();
        await reauthenticateWithPopup(user, provider);
      } else if (providerId === "password") {
        const password = prompt("Please enter your password to confirm:");
        if (!password) {
          alert("Account deletion cancelled.");
          setIsDeletingAccount(false);
          return;
        }
        const credential = EmailAuthProvider.credential(user.email as string, password);
        await reauthenticateWithCredential(user, credential);
      } else {
        throw new Error("Unsupported authentication method for reauthentication.");
      }

      // Clear storage
      await clearAllStorage();

      // Delete Firestore user doc
      await deleteDoc(doc(db, "users", user.uid));

      // Delete Auth account
      await deleteUser(user);

      toast({
        title: "Account Deleted",
        description: "Your account has been permanently deleted.",
        duration: 3000,
      });
      window.location.assign("/admin-login");
    } catch (error: any) {
      console.error("Account deletion error:", error);
      toast({
        title: "Account Deletion Failed",
        description: error.message || "An error occurred while deleting your account.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsDeletingAccount(false);
      localStorage.removeItem("loggingOut");
    }
  };

  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSuggestion(null);

    try {
      // Get current school ID from auth/store if possible, or pass null
      // We can access the store state but for simplicity we'll let aiService handle auth if strict
      // or we can grab it from redux.
      // In Header.tsx we have access to auth.currentUser but claims are complex.
      // Let's pass 'undefined' for schoolID and let aiService wrapper logic handle it or simple logic.
      // Actually, looking at ChatbotPage, it fetches claims.
      // Ideally we should get the schoolID.
      // For now, let's just run it. The `aiService` uses `initializeModel` which calls `getModelForFeature`.
      // If schoolId is missing, it might fallback or use global config.
      
      const result = await import("@/services/aiService").then(mod => mod.generateNavigationSuggestion(searchQuery, undefined, 'admin'));
      
      if (result && result.route) {
        setSuggestion(result);
      } else {
        toast({
            title: "No route found",
            description: "AI could not find a matching page for your request.",
            variant: "destructive"
        });
      }
    } catch (err) {
      console.error("Search error:", err);
      toast({
        title: "Search failed",
        description: "Could not process your request.",
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleNavigate = () => {
    if (suggestion?.route) {
      navigate(suggestion.route);
      setIsSearchOpen(false);
      setSearchQuery("");
      setSuggestion(null);
    }
  };

  return (
    <>
      <ConfirmDialog
        open={showLogoutDialog}
        title="Log out?"
        description="Are you sure you want to log out?"
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutDialog(false)}
        confirmText="Log out"
        cancelText="Cancel"
      />
      
      {/* Search Dialog */}
      {isSearchOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/50 backdrop-blur-sm"
             onClick={() => setIsSearchOpen(false)}>
            <div className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 p-4 m-4"
                 onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 pb-3 mb-3">
                    <Search className="h-5 w-5 text-indigo-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">AI Navigation Assistant</h3>
                </div>
                
                <form onSubmit={handleSearchSubmit} className="relative">
                    <input
                        autoFocus
                        type="text"
                        placeholder="What would you like to do? (e.g. 'Add a new student')"
                        className="w-full p-3 pl-4 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Button 
                        type="submit" 
                        size="sm"
                        disabled={isSearching || !searchQuery.trim()}
                        className="absolute right-2 top-1.5 h-8 w-8 p-0 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                        {isSearching ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                </form>

                {suggestion && (
                    <div className="mt-4 animate-in fade-in slide-in-from-top-2">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">Suggested Route</p>
                        <div 
                            className="flex items-center justify-between p-3 rounded-lg border border-indigo-100 dark:border-indigo-900/30 bg-indigo-50 dark:bg-indigo-900/10 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 cursor-pointer transition-colors"
                            onClick={handleNavigate}
                        >
                            <div>
                                <h4 className="font-semibold text-indigo-900 dark:text-indigo-100">{suggestion.label}</h4>
                                <p className="text-sm text-indigo-700 dark:text-indigo-300">{suggestion.reason}</p>
                            </div>
                            <Button size="sm" variant="ghost" className="text-indigo-600 dark:text-indigo-400">
                                Go <span className="ml-1">→</span>
                            </Button>
                        </div>
                    </div>
                )}
                
                <div className="mt-4 flex justify-between items-center text-xs text-gray-400 dark:text-gray-500">
                    <span>Powered by Gemini AI</span>
                    <span className="flex gap-1">Press <kbd className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">Enter</kbd> to search</span>
                </div>
            </div>
        </div>
      )}

      <header className={cn("w-full bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between", className)}>
        <div className="flex items-center space-x-2 sm:space-x-4">
          <SidebarTrigger className="p-1 sm:p-2" />
          <div className="flex items-center">
            <div className="flex items-center justify-center">
              <img
                src={SonomaLogo}
                alt="Sonoma Logo"
                className="h-[22px] w-[22px] sm:h-[32.5px] sm:w-[32.5px] rounded-md"
                style={{
                  display: 'block',
                  boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
                  WebkitFilter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))',
                  filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.18)) contrast(1.2)',
                  background: 'white',
                }}
              />
            </div>
            <div className="flex flex-col leading-tight ml-2 sm:ml-3">
              <span className="text-sm sm:text-lg font-semibold text-gray-800 dark:text-white">
                Sonoma Cloud
              </span>
              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                Admin Portal
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-1 sm:space-x-3">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            onClick={() => setIsSearchOpen(true)}
          >
            <Search className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
            <Bell className="h-4 w-4" />
          </Button>
          <DarkModeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                <User className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
            >
              <DropdownMenuLabel className="text-gray-900 dark:text-white">My Account</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />
              <DropdownMenuItem className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                Account Preferences
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />
              <DropdownMenuItem 
                onClick={() => setShowSwitchModal(true)}
                className="text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              >
                Switch to Staff View
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />
              <DropdownMenuItem
                onClick={handleDeleteAccount}
                className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900"
                disabled={isDeletingAccount}
              >
                {isDeletingAccount ? "Deleting Account..." : "Delete Account"}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900"
                disabled={isLoggingOut}
              >
                {isLoggingOut ? "Logging Out..." : "Logout"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      
      {/* Switch View Modal */}
      {showSwitchModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 text-left align-middle shadow-xl transition-all border border-gray-200 dark:border-gray-700 animate-in zoom-in-95 duration-200 scale-100">
            
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                {isSwitchingView ? (
                  <div className="h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <User className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                )}
              </div>
              
              <h3 className="text-xl font-semibold leading-6 text-gray-900 dark:text-gray-100">
                {isSwitchingView ? "Switching View..." : "Switch to Staff View"}
              </h3>
              
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isSwitchingView 
                  ? "Please wait while we set up your staff environment. This ensures you see exactly what your staff sees."
                  : "You are about to switch to the Staff Portal view. You will see the application exactly as a staff member would."
                }
              </p>

              {!isSwitchingView && (
                <div className="flex gap-3 w-full mt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowSwitchModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={handleSwitchToStaffView}
                  >
                    Confirm Switch
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;